import { __assign, __rest } from "tslib";
import React, { useState } from 'react';
import cx from 'classnames';
import bcx from 'bem-classname';
import ReactSelect, { components } from 'react-select';
import { ChevronDown, IconAlert, IconCheck, Search } from '../icons';
import { reactSelectStyle } from './selectStyles';
var Option = components.Option;
var componentClass = 'select-custom';
function DefaultOption(props) {
    return (React.createElement(Option, __assign({}, props),
        React.createElement("div", { ref: props.innerRef, className: "d-flex justify-content-between", "data-testid": props.data.dataTestId },
            props.data.label,
            props.isSelected ? React.createElement(IconCheck, { style: { color: 'var(--color-informative-50)' } }) : null)));
}
function TextWithIconOption(props) {
    return (React.createElement(Option, __assign({}, props),
        React.createElement("div", { key: props.data.value, ref: props.innerRef, className: cx('textwithicon', {
                'textwithicon--large': props.selectProps.variant === 'textWithIconLarge',
            }), "data-testid": props.data.dataTestId },
            props.data.thumbnail && React.createElement("img", { alt: props.data.title, src: props.data.thumbnail }),
            React.createElement("span", { className: "textwithicon__title" }, props.data.title),
            React.createElement("span", { className: "textwithicon__subtitle" }, props.data.subtitle),
            props.isSelected ? React.createElement(IconCheck, { className: "textwithicon__check" }) : null)));
}
function TextWithDescriptionOption(props) {
    return (React.createElement(Option, __assign({}, props),
        React.createElement("div", { ref: props.innerRef, key: props.data.value, className: "d-flex justify-content-between", "data-testid": props.data.dataTestId },
            React.createElement("div", { className: "u-p-1" },
                React.createElement("div", { className: "text--headings text--lg" }, props.data.label),
                React.createElement("div", { className: "text--base text--sm u-mt-1" }, props.data.description)),
            props.isSelected ? React.createElement(IconCheck, { style: { color: 'var(--color-informative-50)' } }) : null)));
}
function DefaultInput(props) {
    return (React.createElement("div", { ref: props.innerRef, className: "d-flex justify-content-between" },
        !props.selectProps.isValid ? (React.createElement(IconAlert, { className: "text--danger mt-1 mr-2", size: "18" })) : null,
        React.createElement(ChevronDown, { className: cx('mr-4', bcx(componentClass, 'dropdown-icon', {
                disabled: props.selectProps.isDisabled,
                active: props.hasValue,
            })), size: "24" })));
}
function TextWithIconInput(props) {
    if (!props.selectProps.value) {
        return React.createElement(DefaultInput, __assign({ ref: props.innerRef }, props));
    }
    return (React.createElement("div", { key: props.selectProps.value, ref: props.innerRef, className: cx('textwithicon', bcx('textwithicon', 'selected', { disabled: props.selectProps.isDisabled }), { 'textwithicon--large': props.selectProps.variant === 'textWithIconLarge' }) },
        props.selectProps.value.thumbnail && (React.createElement("img", { alt: props.selectProps.value.title, src: props.selectProps.value.thumbnail, className: "textwithicon__thumbnail" })),
        React.createElement("span", { className: bcx('textwithicon', 'title') }, props.selectProps.value.title),
        React.createElement("span", { className: bcx('textwithicon', 'subtitle') }, props.selectProps.value.subtitle),
        !props.selectProps.isValid ? (React.createElement(IconAlert, { className: cx('text--danger', 'mt-1', bcx('textwithicon', 'invalid-icon')), size: "18" })) : null,
        React.createElement(ChevronDown, { className: bcx('textwithicon', 'dropdown-icon'), size: "24" })));
}
function SearchInput(props) {
    return (React.createElement("div", { ref: props.innerRef, className: "d-flex justify-content-between" },
        React.createElement(Search, { className: cx('mr-4', bcx(componentClass, 'dropdown-icon', {
                disabled: props.selectProps.isDisabled,
                active: props.hasValue,
            })), size: "24" })));
}
function getSelectValue(value, options) {
    if (options === void 0) { options = []; }
    if (!value || !options || options.length === 0)
        return undefined;
    if (Array.isArray(value) && value.length) {
        var selectedOptions = [];
        if (typeof value[0] === 'string') {
            var _loop_1 = function (i) {
                var selectedOption = value[i];
                var targetOption = options.find(function (opt) { return opt.value === selectedOption; });
                if (targetOption) {
                    selectedOptions.push(targetOption);
                }
            };
            for (var i = 0; i < value.length; i += 1) {
                _loop_1(i);
            }
            return selectedOptions;
        }
        return value;
    }
    return typeof value === 'string' ? options.find(function (opt) { return opt.value === value; }) : value;
}
var INPUTS_BY_VARIANT = {
    default: {
        option: DefaultOption,
        input: DefaultInput,
    },
    search: {
        option: DefaultOption,
        input: SearchInput,
    },
    textWithIconSmall: {
        option: TextWithIconOption,
        input: TextWithIconInput,
    },
    textWithIconLarge: {
        option: TextWithIconOption,
        input: TextWithIconInput,
    },
    textWithDescription: {
        option: TextWithDescriptionOption,
        input: DefaultInput,
    },
};
export var SelectText = function (props) {
    var id = props.id, _a = props.inputId, inputId = _a === void 0 ? '' : _a, label = props.label, className = props.className, value = props.value, name = props.name, defaultValue = props.defaultValue, _b = props.variant, variant = _b === void 0 ? 'default' : _b, _c = props.disabled, disabled = _c === void 0 ? false : _c, _d = props.invalid, invalid = _d === void 0 ? false : _d, onChange = props.onChange, onBlur = props.onBlur, isMulti = props.isMulti, placeholder = props.placeholder, _e = props.helperText, helperText = _e === void 0 ? '' : _e, _f = props.isOptionDisabled, isOptionDisabled = _f === void 0 ? function (_a) {
        var disabled = _a.disabled;
        return !!disabled;
    } : _f, options = props.options, otherProps = __rest(props, ["id", "inputId", "label", "className", "value", "name", "defaultValue", "variant", "disabled", "invalid", "onChange", "onBlur", "isMulti", "placeholder", "helperText", "isOptionDisabled", "options"]);
    var _g = useState(false), focused = _g[0], setFocused = _g[1];
    var classes = cx(className, bcx(componentClass, {}));
    var _h = INPUTS_BY_VARIANT[variant] || {}, option = _h.option, input = _h.input;
    var currentValue = getSelectValue(value, options);
    var handleFocus = function () {
        setFocused(true);
    };
    var handleBlur = function (e) {
        setFocused(false);
        onBlur === null || onBlur === void 0 ? void 0 : onBlur(e);
    };
    return (React.createElement("div", { className: classes },
        label && (React.createElement("label", { className: cx('text--sm', bcx(componentClass, 'label', {
                disabled: disabled,
                active: focused || !!(Array.isArray(value) ? value.length : value),
            })), htmlFor: id }, label)),
        React.createElement(ReactSelect, __assign({}, otherProps, { id: id, inputId: inputId, name: name, isMulti: isMulti, isDisabled: disabled, isOptionDisabled: isOptionDisabled, options: options, placeholder: placeholder, value: currentValue, defaultValue: defaultValue, onFocus: handleFocus, onChange: onChange, onBlur: handleBlur, components: {
                Option: option || DefaultOption,
                IndicatorsContainer: input || DefaultInput,
            }, styles: reactSelectStyle(invalid), isValid: !invalid, variant: variant, isSearchable: variant !== 'textWithIconLarge' })),
        helperText && (React.createElement("div", { className: cx('text--sm', bcx(componentClass, 'helper-text', {
                disabled: disabled,
                invalid: invalid,
            })) }, helperText))));
};
//# sourceMappingURL=SelectText.js.map